import {
  useRef,
  useCallback,
  useEffect,
  useState,
  MouseEvent,
} from 'react';

export default function useRecordedAudioVisualizer() {
  const containerRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameId = useRef<number>(0);
  const waveformData = useRef<number[]>([]);
  const isAnimatingRef = useRef(false);
  const audioDurationRef = useRef<number>(0);
  const playStartTimeRef = useRef<number>(0);
  const pausedAtRef = useRef<number>(0);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const drawProgressIndicator = useCallback((progress: number) => {
    if (containerRef.current && waveformData.current.length > 0) {
      const canvas = containerRef.current;
      const canvasCtx = canvas.getContext('2d')!;

      // Clear canvas with background color
      canvasCtx.fillStyle = '#EFF6FF';
      canvasCtx.fillRect(0, 0, canvas.width, canvas.height);

      const barWidth = 2;
      const gapWidth = 1;
      const totalWidth = barWidth + gapWidth;
      const progressX = progress * canvas.width;

      // Draw waveform bars with different colors based on progress
      waveformData.current.forEach((height, index) => {
        const x = index * totalWidth;
        const barHeight = Math.max(1, height); // Minimum height of 1px
        const y = (canvas.height - barHeight) / 2;

        // Determine color based on position relative to progress
        if (x < progressX) {
          canvasCtx.fillStyle = '#60A5FA'; // Blue-400 for played portion
        } else {
          canvasCtx.fillStyle = '#BFDBFE'; // Blue 200 for unplayed portion
        }

        canvasCtx.fillRect(x, y, barWidth, barHeight);
      });

      // Draw progress line
      const progressXLine = Math.floor(progressX);
      canvasCtx.fillStyle = '#1E40AF'; // Darkest blue for progress indicator
      canvasCtx.fillRect(progressXLine, 0, 1, canvas.height);
    }
  }, []);

  const analyzeAudioBlob = useCallback(async (blob: Blob) => {
    if (containerRef.current) {
      setIsAnalyzing(true);

      try {
        // Create audio context if it doesn't exist
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        // Convert blob to array buffer
        const arrayBuffer = await blob.arrayBuffer();
        const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);

        // Store the audio duration for calculations
        audioDurationRef.current = audioBuffer.duration;

        const canvas = containerRef.current;
        const canvasWidth = canvas.width;
        const numberOfBars = Math.floor(canvasWidth / 3); // 2px bar + 1px gap

        // Get the raw audio data
        const rawData = audioBuffer.getChannelData(0); // Use first channel
        const samplesPerBar = Math.floor(rawData.length / numberOfBars);

        // Calculate amplitude for each bar
        const amplitudes: number[] = [];

        for (let i = 0; i < numberOfBars; i += 1) {
          const start = i * samplesPerBar;
          const end = Math.min(start + samplesPerBar, rawData.length);

          let sum = 0;
          for (let j = start; j < end; j += 1) {
            sum += Math.abs(rawData[j]);
          }

          const average = sum / (end - start);
          amplitudes.push(average);
        }

        // Normalize amplitudes to canvas height
        const maxAmplitude = Math.max(...amplitudes);
        waveformData.current = amplitudes.map((amp) =>
          (amp / maxAmplitude) * canvas.height * 0.8);

        // Draw the initial waveform (all unplayed)
        drawProgressIndicator(0);
      } catch (error) {
        console.error('[Snap] Error analyzing audio:', error);
      } finally {
        setIsAnalyzing(false);
      }
    }
  }, [drawProgressIndicator]);

  const startProgressAnimation = useCallback((audioElement: HTMLAudioElement) => {
    if (isAnimatingRef.current || !audioDurationRef.current) return;

    isAnimatingRef.current = true;
    // Account for previous playback
    playStartTimeRef.current = Date.now() - (pausedAtRef.current * 1000);

    const updateProgress = () => {
      if (!isAnimatingRef.current || audioElement.paused) {
        isAnimatingRef.current = false;
        return;
      }

      const currentTime = (Date.now() - playStartTimeRef.current) / 1000;
      const progress = Math.min(currentTime / audioDurationRef.current, 1);

      drawProgressIndicator(progress);

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(updateProgress);
      } else {
        isAnimatingRef.current = false;
        pausedAtRef.current = 0; // Reset when audio ends
      }
    };

    updateProgress();
  }, [drawProgressIndicator]);

  const stopProgressAnimation = useCallback(() => {
    if (isAnimatingRef.current) {
      // Store current progress when pausing
      const currentTime = (Date.now() - playStartTimeRef.current) / 1000;
      pausedAtRef.current = Math.min(currentTime, audioDurationRef.current);
    }
    isAnimatingRef.current = false;
    cancelAnimationFrame(animationFrameId.current);
  }, []);

  const resetProgress = useCallback(() => {
    pausedAtRef.current = 0;
    playStartTimeRef.current = 0;
    isAnimatingRef.current = false;
    cancelAnimationFrame(animationFrameId.current);
    drawProgressIndicator(0); // Reset to initial state (0% progress)
  }, [drawProgressIndicator]);

  const handleCanvasClick = useCallback(
    (
      event: MouseEvent<HTMLCanvasElement>,
      audioElement: HTMLAudioElement,
      onPlay?: () => void,
    ) => {
      if (!containerRef.current || !audioDurationRef.current) return;

      const canvas = containerRef.current;
      const rect = canvas.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const progress = Math.max(0, Math.min(1, clickX / canvas.width));
      const newTime = progress * audioDurationRef.current;

      // Update our internal tracking
      pausedAtRef.current = newTime;

      // Set the audio element time
      try {
        audioElement.currentTime = newTime;
      } catch (error) {
        console.warn('[Snap] Failed to set currentTime:', error);
      }

      // Update the visual immediately
      drawProgressIndicator(progress);

      // If audio is paused or never played, start playing
      if (audioElement.paused) {
        audioElement.play().then(() => {
          // Trigger the play handler to start our animation
          if (onPlay) {
            onPlay();
          }
          // Set up our timing for the new position
          playStartTimeRef.current = Date.now() - (newTime * 1000);
        }).catch((error) => {
          console.error('[Snap] Failed to start audio playback:', error);
        });
      } else {
        // If currently playing, just restart the animation from the new position
        playStartTimeRef.current = Date.now() - (newTime * 1000);
      }
    },
    [drawProgressIndicator],
  );

  // Cleanup on unmount
  useEffect(() => () => {
    stopProgressAnimation();
  }, [stopProgressAnimation]);

  return {
    containerRef,
    analyzeAudioBlob,
    drawProgressIndicator,
    startProgressAnimation,
    stopProgressAnimation,
    resetProgress,
    handleCanvasClick,
    isAnalyzing,
  };
}
